import { Gender } from './enums';
import { slopeEstimationPercentile, interceptEstimationPercentile, smoothPercentileInclusive } from './shared-util';
import { LogRecordState } from './data-model/user-log';
import { Log_WeighIn, getAverageCalipersSum, getAverageWaist, hasMeasuredBodyFat } from './data-model/log-weigh-in';
import { DateTime } from 'luxon';

/**
 * Recommended body-composition (caliper / tape) measurement cadence for a new
 * user — three days. Applied when the user has no prior Done body-comp entry.
 *
 * See `design-docs/body-comp-measurement-cadence.md` (Phase 2) for the
 * rationale: at the historical 5-day default, a 14-day window typically only
 * holds ~3 caliper or tape points, which routinely trips the Phase-1 sample-
 * count gate (`MIN_CALIPER_SAMPLES_FOR_CUT` / `MIN_TAPE_SAMPLES_FOR_CUT` in
 * `plan-update.ts`). Tightening new-user cadence lets the gate produce
 * adjustments on normal usage instead of `InsufficientFatnessConfidence`.
 */
export const BF_FREQUENCY_NEW_USER_DAYS = 3;

/**
 * Recommended body-composition cadence for users who already have at least one
 * Done body-comp entry — five days. Grandfathers established users so the
 * Phase 2 default change does not disrupt their existing rhythm.
 *
 * Five days gives 3 readings in the plan check's 14-day window, which is enough
 * for the trend but not for a calorie cut on an unclear trend. When the plan
 * check needs more, it says so (`needsMoreBodyFatReadings`) and the weigh-in
 * page asks for a reading whatever the rhythm says (#6067).
 */
export const BF_FREQUENCY_EXISTING_USER_DAYS = 5;

/**
 * Returns true if the WeighIn entry is Done and carries at least one populated
 * tape (`waist_cm[0] > 0`) or caliper (`suprailiac_mm[0] > 0`) reading.
 *
 * Used to decide whether a user already has a body-comp measurement history
 * for the purposes of cadence grandfathering. A Done weight-only entry does
 * NOT count — only entries with actual tape or caliper data matter, since the
 * cadence governs body-fat measurement specifically.
 *
 * "Carries a real reading" is `hasMeasuredBodyFat` in `./data-model/log-weigh-in.ts`, so this helper and
 * every screen asking the same question share one definition — including the
 * deliberate suprailiac-only caliper gate and the ignored deprecated tape
 * fields, both explained in the comment block above those readers there.
 */
function hasBodyCompData(entry: Log_WeighIn): boolean {
  if (entry.state !== LogRecordState.Done) return false;
  return hasMeasuredBodyFat(entry);
}

/**
 * Phase 3 noise terciles for caliper signal (rms/iqr from rolling Theil-Sen
 * residuals, ±14-day window). Source: Report 11 of
 * `analyses/weighin-tracking-quality/results/executive-summary.md`. Caliper
 * is the cleaner signal per sample (Reports 4a / 5) so its boundaries sit
 * below tape's. rms/iqr ≤ low → 5-day cadence; between low and high →
 * 3-day; above high → 2-day.
 */
export const CALIPER_NOISE_LOW_THRESHOLD = 0.27;
export const CALIPER_NOISE_HIGH_THRESHOLD = 0.51;

/**
 * Phase 3 noise terciles for tape signal (rms/iqr from rolling Theil-Sen
 * residuals, ±14-day window). Source: Report 11 of the analysis. Tape is
 * noisier than caliper, so the same buckets translate to higher absolute
 * thresholds.
 */
export const TAPE_NOISE_LOW_THRESHOLD = 0.37;
export const TAPE_NOISE_HIGH_THRESHOLD = 0.58;

/** Phase 3 cadence for users whose observed noise is in the low tercile. */
export const BF_FREQUENCY_LOW_NOISE_DAYS = 5;
/**
 * Phase 3 cadence for users whose observed noise is in the moderate tercile.
 * Matches the Phase 2 new-user default.
 */
export const BF_FREQUENCY_MID_NOISE_DAYS = 3;
/** Phase 3 cadence for users whose observed noise is in the high tercile. */
export const BF_FREQUENCY_HIGH_NOISE_DAYS = 2;

/**
 * Phase 3 eligibility: noise computation requires at least this many Done
 * body-comp samples of a given signal in the last 60 days, matching the
 * rolling-Theil-Sen helper's minimum (≥6 points to compute residual RMS
 * with at least three windowed fits).
 */
const NOISE_MIN_SAMPLES = 6;

/**
 * Phase 3 window: noise is computed from the user's Done entries in the
 * most recent 60 days only. Older data is excluded — cadence should track
 * the user's *current* measurement noise, not their lifetime history.
 */
const NOISE_WINDOW_DAYS = 60;

/**
 * Half-width (days) of the rolling Theil-Sen residual window used by
 * `windowedResidualRms`. Each fit covers the points within ±14 days of the
 * anchor point. Ported from `report3a_inRunNoise()` in
 * `analyses/weighin-tracking-quality/weighin-analyze.ts`.
 */
const RESIDUAL_WINDOW_DAYS = 14;

/**
 * Minimum number of points inside a single ±14-day window for the local
 * Theil-Sen fit to be considered well-posed. Matches the analysis reference.
 */
const RESIDUAL_WINDOW_MIN_POINTS = 4;

/**
 * Minimum number of residuals required to produce a meaningful rms.
 * Matches the analysis reference.
 */
const RESIDUAL_MIN_COUNT = 3;

/**
 * Interquartile range of `values`, or undefined if the set is too small or
 * empty. Used as the per-series normalizer for residual RMS, so that the
 * resulting rms/iqr is comparable across users with different absolute
 * tape / caliper magnitudes.
 */
function iqr(values: number[]): number | undefined {
  if (values.length === 0) return undefined;
  const q25 = smoothPercentileInclusive(values, 0.25);
  const q75 = smoothPercentileInclusive(values, 0.75);
  if (q25 === undefined || q75 === undefined) return undefined;
  return q75 - q25;
}

/**
 * Sample point for noise computation: `x` is days from a reference time
 * (negative for past entries), `y` is the signal value (mm for calipers,
 * cm for tape).
 */
interface NoiseSample { x: number; y: number; }

/**
 * Rolling Theil-Sen residual RMS, normalized by the series IQR. Implements
 * the math from `report3a_inRunNoise()` in
 * `analyses/weighin-tracking-quality/weighin-analyze.ts` (the simplest
 * reference variant, no outlier filtering) using the project's existing
 * `slopeEstimationPercentile` / `interceptEstimationPercentile` helpers
 * at percentile 0.5 (median Theil-Sen).
 *
 * Note on median definition: the project helpers route through
 * `smoothPercentileInclusive`, which linearly interpolates between the
 * two middle elements when the slope/intercept array has even length.
 * The analysis reference uses raw upper-of-the-two indexing
 * (`slopes[Math.floor(slopes.length/2)]`), which can diverge by up to
 * one element-step per window. The Report-11 tercile thresholds
 * (0.27 / 0.51 / 0.37 / 0.58) are robust to this small calibration drift
 * — tercile cutoffs are inherently approximate and the difference is
 * well inside bucket widths.
 *
 * Returns `undefined` when there are <6 points, when the series IQR is 0
 * (flat / constant signal — rms/iqr would be undefined), or when fewer
 * than 3 windowed fits succeed.
 */
function windowedResidualRms(samples: NoiseSample[]): number | undefined {
  if (samples.length < NOISE_MIN_SAMPLES) return undefined;
  const seriesIqr = iqr(samples.map(p => p.y));
  if (seriesIqr === undefined || seriesIqr === 0) return undefined;

  const residuals: number[] = [];
  // for each anchor point, fit a local Theil-Sen line over the ±14d
  // neighbourhood and record this anchor's residual against that fit
  for (const anchor of samples) {
    const neighbours = samples.filter(p => Math.abs(p.x - anchor.x) <= RESIDUAL_WINDOW_DAYS);
    // if the local window is too sparse, skip — the fit would be unreliable
    if (neighbours.length < RESIDUAL_WINDOW_MIN_POINTS) continue;
    // slopeEstimationPercentile's signature is `number` but
    // smoothPercentileInclusive can return undefined when the slopes array
    // is empty (a window where all x's coincide — possible if all
    // neighbours fall on the same `daysFromEnd` due to log-time rounding).
    // The undefined guards below look dead to the type checker but are
    // load-bearing for that degenerate case.
    const slope = slopeEstimationPercentile(neighbours, 0.5);
    if (slope === undefined || !isFinite(slope)) continue;
    const intercept = interceptEstimationPercentile(neighbours, slope, 0.5);
    if (intercept === undefined || !isFinite(intercept)) continue;
    residuals.push(anchor.y - (slope * anchor.x + intercept));
  }
  if (residuals.length < RESIDUAL_MIN_COUNT) return undefined;

  // compute root-mean-square of residuals, normalized by series IQR so
  // the score is comparable across users with different absolute scales
  const sumSq = residuals.reduce((s, r) => s + r * r, 0);
  const rms = Math.sqrt(sumSq / residuals.length);
  return rms / seriesIqr;
}

export interface BodyCompNoiseResult {
  /** Normalized noise score: residual RMS divided by series IQR. */
  rmsIqr: number;
  /** Which signal the score was computed from. */
  signal: 'caliper' | 'tape';
}

/**
 * Build a NoiseSample array for the given signal extractor. Skips entries
 * that fall outside the 60-day window, are not in `Done` state, or for
 * which the extractor yields undefined / non-positive. `x` is days from
 * `nowEod` (negative for past entries).
 */
function buildNoiseSamples(
  entries: Log_WeighIn[],
  nowEod: DateTime,
  extractY: (entry: Log_WeighIn) => number | undefined,
): NoiseSample[] {
  const samples: NoiseSample[] = [];
  for (const entry of entries) {
    // if entry is not yet Done (Draft / cancelled / etc.), skip
    if (entry.state !== LogRecordState.Done) continue;
    // Luxon's `diff` is zone-agnostic — it compares absolute instants —
    // so we can parse the UTC timestamp directly without converting zones
    const entryTime = DateTime.fromISO(entry.time_utc, { zone: 'utc' });
    const daysFromEnd = entryTime.diff(nowEod, 'days').days;
    // if entry falls outside the 60-day window (or is implausibly in the
    // future relative to `nowEod`), skip
    if (daysFromEnd > 0 || daysFromEnd < -NOISE_WINDOW_DAYS) continue;
    const y = extractY(entry);
    // if extractor produced no usable value, skip. The `y <= 0` guard is
    // defensive — the analysis reference (`report3a_inRunNoise`) doesn't
    // have it because `getAverageCalipersSum` / `getAverageWaist` already
    // return undefined for empty/invalid data; kept here to protect
    // against any future extractor that returns 0 for "no data"
    if (y === undefined || !isFinite(y) || y <= 0) continue;
    samples.push({ x: daysFromEnd, y });
  }
  return samples;
}

/**
 * Compute the per-user body-composition noise score (rms/iqr) from the
 * user's recent weigh-in log. Returns `undefined` when neither signal has
 * enough recent data to produce a reliable score.
 *
 * Filters to Done WeighIn entries within the last 60 days, builds a
 * `{x: daysFromEnd, y}` sample array, and runs the rolling-Theil-Sen
 * residual-RMS estimator described in Report 3a of the analysis. Caliper
 * is preferred over tape when ≥6 caliper samples are available — Reports
 * 4a / 5 show caliper is the cleaner signal per sample (~10% of raw tape
 * readings are unit-confusion outliers).
 *
 * Caliper extraction uses `getAverageCalipersSum`, which sums per-site
 * averages and requires `gender` to know which sites apply (male: two
 * sites, female: three). Tape extraction uses `getAverageWaist` and is
 * gender-agnostic.
 *
 * Computation is on-demand — ≤60 entries worst case, simple percentile +
 * pairwise-slope Theil-Sen. No caching.
 */
export function computeBodyCompNoise(
  entries: Log_WeighIn[],
  now: DateTime,
  gender: Gender | undefined,
): BodyCompNoiseResult | undefined {
  const nowEod = now.endOf('day');

  // try caliper first when gender is known and we have ≥6 caliper samples
  // — Reports 4a / 5 say caliper is the cleaner signal per sample
  if (gender !== undefined) {
    const caliperSamples = buildNoiseSamples(entries, nowEod, w => getAverageCalipersSum(w, gender));
    if (caliperSamples.length >= NOISE_MIN_SAMPLES) {
      const rmsIqr = windowedResidualRms(caliperSamples);
      if (rmsIqr !== undefined && isFinite(rmsIqr)) {
        return { rmsIqr, signal: 'caliper' };
      }
    }
  }

  // otherwise fall back to tape — gender-agnostic and used when caliper
  // is ineligible (no gender, <6 samples, or flat caliper signal)
  const tapeSamples = buildNoiseSamples(entries, nowEod, w => getAverageWaist(w));
  if (tapeSamples.length >= NOISE_MIN_SAMPLES) {
    const rmsIqr = windowedResidualRms(tapeSamples);
    if (rmsIqr !== undefined && isFinite(rmsIqr)) {
      return { rmsIqr, signal: 'tape' };
    }
  }

  return undefined;
}

/**
 * Map a noise score to the recommended cadence using the tercile
 * thresholds from Report 11 of the analysis. Boundary handling matches
 * the report: `rmsIqr <= low` → low-noise bucket; `low < rmsIqr <= high`
 * → moderate; `rmsIqr > high` → high-noise.
 */
function cadenceFromNoise(result: BodyCompNoiseResult): number {
  const low = result.signal === 'caliper' ? CALIPER_NOISE_LOW_THRESHOLD : TAPE_NOISE_LOW_THRESHOLD;
  const high = result.signal === 'caliper' ? CALIPER_NOISE_HIGH_THRESHOLD : TAPE_NOISE_HIGH_THRESHOLD;
  if (result.rmsIqr <= low) return BF_FREQUENCY_LOW_NOISE_DAYS;
  if (result.rmsIqr <= high) return BF_FREQUENCY_MID_NOISE_DAYS;
  return BF_FREQUENCY_HIGH_NOISE_DAYS;
}

/**
 * Returns the recommended body-composition (caliper / tape) measurement
 * cadence in days for the user whose WeighIn list is `entries`.
 *
 * The check runs over the exact list the caller passes — it does NOT
 * query the database. The noise path internally re-clamps to the last
 * `NOISE_WINDOW_DAYS` (60) days via `buildNoiseSamples`, so callers can
 * pass a wider list without affecting noise computation; the Phase 2
 * fallback uses every entry the caller passes. The only call site today
 * passes the 60-day recent-log window the weigh-in page loads.
 *
 * Phase 3 path (noise-based): when `entries` contains ≥6 Done body-comp
 * samples of caliper (or, failing that, tape) inside the last 60 days,
 * compute the user's own rms/iqr via `computeBodyCompNoise` and pick the
 * cadence by tercile — 5 d (low noise) / 3 d (moderate) / 2 d (high).
 * Caliper preferred over tape (Reports 4a / 5).
 *
 * Phase 2 fallback (signal-based new-vs-existing): when noise cannot be
 * computed, returns `BF_FREQUENCY_NEW_USER_DAYS` (3) if `entries` has no
 * Done WeighIn with a positive `tapeMeasures.waist_cm[0]` or
 * `caliperMeasures.suprailiac_mm[0]` reading; otherwise
 * `BF_FREQUENCY_EXISTING_USER_DAYS` (5) — grandfathering users who
 * already had a 5-day rhythm before Phase 2 tightened the default.
 *
 * `now` and `gender` are required because noise computation needs both —
 * the 60-day window is anchored at `now`, and `getAverageCalipersSum`
 * picks caliper sites by gender. `gender` may be `undefined`, in which
 * case the caliper path is skipped and the helper falls back to tape
 * (or to the Phase 2 path if tape is also insufficient).
 */
export function getRecommendedBfFrequencyDays(
  entries: Log_WeighIn[],
  now: DateTime,
  gender: Gender | undefined,
): number {
  const noise = computeBodyCompNoise(entries, now, gender);
  if (noise !== undefined) return cadenceFromNoise(noise);
  for (const entry of entries) {
    if (hasBodyCompData(entry)) return BF_FREQUENCY_EXISTING_USER_DAYS;
  }
  return BF_FREQUENCY_NEW_USER_DAYS;
}
